// src/resources/reports/ReportPushNotifications/MessagesTable.tsx
//
// "Mensagens enviadas" (§4 bloco 4) — uma linha por mensagem (não por
// disparo: uma campanha automática dispara uma vez por cliente e viraria
// milhares de linhas idênticas, o problema exato que motivou este relatório
// — ver §2 do spec, "Decisão de produto"). Ordenação local: os dados chegam
// prontos do backend (até 100 linhas), não vale ida ao servidor pra reordenar.
import React, { useMemo, useState } from 'react';
import {
  Box, Table, TableBody, TableCell, TableHead, TableRow, TableSortLabel, Tooltip, Typography,
} from '@mui/material';
import moment from 'moment';
import { MessageRow, ORIGIN_LABELS, fmtInt, fmtPctSmall } from './types';

type SortKey = 'title' | 'origin' | 'dispatches' | 'sent' | 'deliveryRate' | 'readRate' | 'clickRate' | 'influencedOpens' | 'firstSentAt' | 'lastSentAt';

// `caption` é a legenda de uma linha embaixo do título da coluna: "Entrega",
// "Influenciadas" e até "Disparos" significam coisas específicas aqui, e o dono
// do posto não deveria ter que descer até a nota de rodapé pra descobrir.
const COLUMNS: { key: SortKey; label: string; caption?: string; align?: 'right' }[] = [
  { key: 'title', label: 'Mensagem' },
  { key: 'origin', label: 'Tipo' },
  { key: 'dispatches', label: 'Disparos', caption: 'vezes que saiu', align: 'right' },
  { key: 'sent', label: 'Enviadas', caption: 'aparelhos alcançados', align: 'right' },
  { key: 'deliveryRate', label: 'Confirmação', caption: 'app avisou que recebeu', align: 'right' },
  { key: 'readRate', label: 'Leitura', caption: 'tocou na notificação', align: 'right' },
  { key: 'clickRate', label: 'Clique', caption: 'abriu a tela indicada', align: 'right' },
  // §5 do spec — mesma mensagem, mas quanta gente abriu o app sem tocar na notificação.
  { key: 'influencedOpens', label: 'Influenciadas', caption: 'voltou ao app sem tocar', align: 'right' },
  { key: 'firstSentAt', label: 'Primeiro envio', align: 'right' },
  { key: 'lastSentAt', label: 'Último envio', align: 'right' },
];

/**
 * Compara já aplicando a direção, porque "sem dado" (null) tem que ficar SEMPRE no fim —
 * multiplicar o resultado por -1 no desc jogava a mensagem sem taxa pro topo, acima da
 * campanha com 60% de leitura.
 */
const compare = (a: MessageRow, b: MessageRow, key: SortKey, dir: 'asc' | 'desc'): number => {
  const va = a[key] as any;
  const vb = b[key] as any;
  if (va === null || va === undefined) return (vb === null || vb === undefined) ? 0 : 1;
  if (vb === null || vb === undefined) return -1;
  const cmp = typeof va === 'string' ? va.localeCompare(vb, 'pt-BR') : va - vb;
  return dir === 'asc' ? cmp : -cmp;
};

const fmtDateTime = (iso: string): string => moment(iso).format('DD/MM/YYYY HH:mm');

const MessagesTable = ({ messages, messagesTruncated, messagesTotal }: {
  messages: MessageRow[]; messagesTruncated: boolean; messagesTotal: number;
}) => {
  const [sortKey, setSortKey] = useState<SortKey>('sent');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const sorted = useMemo(() => {
    const copy = [...messages];
    copy.sort((a, b) => compare(a, b, sortKey, sortDir));
    return copy;
  }, [messages, sortKey, sortDir]);

  const handleSort = (key: SortKey) => {
    if (key === sortKey) {
      setSortDir(dir => (dir === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
  };

  return (
    <Box sx={{ bgcolor: '#fff', border: '1px solid #e2e8f0', borderRadius: 2, p: 2, mb: 2, overflowX: 'auto' }}>
      <Typography sx={{ fontWeight: 700, fontSize: 14 }}>Mensagens enviadas</Typography>
      <Typography sx={{ fontSize: 12, color: '#64748b', mb: 1.5 }}>
        {fmtInt(messagesTotal)} mensagens no período{messagesTruncated ? ` — mostrando as ${fmtInt(messages.length)} maiores por envio` : ''}
      </Typography>
      {!sorted.length ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
          Nenhuma mensagem enviada no período.
        </Typography>
      ) : (
        <Table size="small" sx={{ minWidth: 900 }}>
          <TableHead>
            <TableRow>
              {COLUMNS.map(col => (
                <TableCell key={col.key} align={col.align} sx={{ bgcolor: '#f8fafc', whiteSpace: 'nowrap' }}>
                  <TableSortLabel active={sortKey === col.key} direction={sortKey === col.key ? sortDir : 'desc'} onClick={() => handleSort(col.key)}>
                    {col.label}
                  </TableSortLabel>
                  {col.caption && (
                    <Typography component="div" sx={{ fontSize: 10, fontWeight: 400, color: '#94a3b8', lineHeight: 1.3 }}>
                      {col.caption}
                    </Typography>
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {sorted.map(row => (
              <TableRow key={row.messageId} hover>
                <TableCell sx={{ maxWidth: 260 }}>
                  <Typography sx={{ fontSize: 12.5, fontWeight: 600 }}>{row.title}</Typography>
                  {row.pushMessage && (
                    <Tooltip title={row.pushMessage}>
                      <Typography sx={{ fontSize: 11, color: '#94a3b8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {row.pushMessage}
                      </Typography>
                    </Tooltip>
                  )}
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{ORIGIN_LABELS[row.origin]}</TableCell>
                <TableCell align="right">{fmtInt(row.dispatches)}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>{fmtInt(row.sent)}</TableCell>
                <TableCell align="right">{fmtPctSmall(row.deliveryRate)}</TableCell>
                <TableCell align="right">{fmtPctSmall(row.readRate)}</TableCell>
                <TableCell align="right">{fmtPctSmall(row.clickRate)}</TableCell>
                <TableCell align="right">{fmtInt(row.influencedOpens ?? 0)}</TableCell>
                <TableCell align="right" sx={{ whiteSpace: 'nowrap', fontSize: 11.5 }}>{fmtDateTime(row.firstSentAt)}</TableCell>
                <TableCell align="right" sx={{ whiteSpace: 'nowrap', fontSize: 11.5 }}>{fmtDateTime(row.lastSentAt)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Box>
  );
};

export default MessagesTable;
